import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
const API_BASE = process.env.REACT_APP_REACT_APP_API_BASE_URL || 'http://localhost:5000/api';
export default function CandidateDocumentUpload() {
    const { id } = useParams();
    const [searchParams] = useSearchParams();
    const sig = searchParams.get('sig') || '';
    const [context, setContext] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    // Files staged per doc key across every section — nothing is uploaded,
    // and no candidate folder is created, until the single Submit button
    // below is clicked.
    const [pendingFiles, setPendingFiles] = useState({});
    const [submitting, setSubmitting] = useState(false);
    const [justSubmitted, setJustSubmitted] = useState(false);
    const loadContext = () => {
        if (!id || !sig) {
            setError('This link is invalid.');
            setLoading(false);
            return;
        }
        fetch(`${API_BASE}/applicant-records/${id}/document-upload-context?sig=${encodeURIComponent(sig)}`)
            .then((r) => r.json())
            .then((res) => {
            if (!res.success) {
                setError(res.message || 'This link could not be verified.');
                setLoading(false);
                return;
            }
            setContext(res.data);
            setLoading(false);
        })
            .catch(() => { setError('Something went wrong loading this page.'); setLoading(false); });
    };
    useEffect(loadContext, [id, sig]);
    const uploadedFor = (docType) => (context?.uploadedDocuments || []).filter((d) => d.docType === docType);
    const handleFileChange = (key, fileList) => {
        setPendingFiles((prev) => ({ ...prev, [key]: fileList ? Array.from(fileList) : [] }));
    };
    const totalStaged = Object.values(pendingFiles).reduce((sum, files) => sum + files.length, 0);
    const handleSubmit = async () => {
        if (totalStaged === 0)
            return;
        setSubmitting(true);
        setError('');
        setJustSubmitted(false);
        try {
            const formData = new FormData();
            for (const [key, files] of Object.entries(pendingFiles)) {
                files.forEach((f) => formData.append(key, f));
            }
            const res = await fetch(`${API_BASE}/applicant-records/${id}/upload-documents?sig=${encodeURIComponent(sig)}`, {
                method: 'POST',
                body: formData,
            });
            const data = await res.json();
            if (!res.ok || !data.success) {
                setError(data.message || 'Failed to upload — please try again.');
                return;
            }
            setPendingFiles({});
            setJustSubmitted(true);
            loadContext();
        }
        catch {
            setError('Failed to upload — please try again.');
        }
        finally {
            setSubmitting(false);
        }
    };
    if (loading) {
        return _jsx("div", { className: "min-h-screen flex items-center justify-center text-gray-500", children: "Loading\u2026" });
    }
    if (error && !context) {
        return (_jsx("div", { className: "min-h-screen flex items-center justify-center bg-gray-50 text-center px-4", children: _jsx("p", { className: "text-gray-600", children: error }) }));
    }
    return (_jsxs("div", { className: "min-h-screen bg-gradient-to-b from-slate-50 to-white", children: [_jsx("nav", { className: "bg-slate-800 shadow-xl", children: _jsx("div", { className: "container mx-auto px-6 py-6", children: _jsx("h1", { className: "text-white text-2xl sm:text-3xl font-bold", children: "Document Upload" }) }) }), _jsxs("div", { className: "container mx-auto px-6 py-10 max-w-2xl", children: [_jsxs("div", { className: "bg-white rounded-xl shadow p-6 mb-6 space-y-1.5", children: [_jsx("p", { className: "text-sm text-gray-500", children: "Candidate" }), _jsx("p", { className: "text-xl font-bold text-slate-800", children: context?.full_name }), _jsx("p", { className: "text-sm text-gray-600", children: context?.designation }), context?.joiningDate && (_jsxs("p", { className: "text-sm text-gray-600", children: [_jsx("span", { className: "font-semibold text-gray-500", children: "Joining Date:" }), ' ', new Date(context.joiningDate).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })] })), _jsx("p", { className: "text-sm text-gray-500 pt-2", children: "Select each document below, then submit them all together. You can come back and submit more anytime before your joining day." })] }), justSubmitted && !error && (_jsx("p", { className: "text-sm text-lime-700 bg-lime-50 border border-lime-200 rounded-lg px-4 py-2 mb-4", children: "Documents submitted successfully." })), error && _jsx("p", { className: "text-red-600 text-sm mb-4", children: error }), _jsx("div", { className: "space-y-4", children: context?.requiredDocuments.map((doc) => {
                            const uploaded = uploadedFor(doc.key);
                            const staged = pendingFiles[doc.key] || [];
                            return (_jsxs("div", { className: "bg-white rounded-xl shadow p-5", children: [_jsx("p", { className: "text-sm font-semibold text-gray-700 mb-2", children: doc.label }), uploaded.length > 0 && (_jsx("ul", { className: "mb-3 space-y-1", children: uploaded.map((u, i) => (_jsx("li", { className: "text-xs", children: _jsxs("a", { href: u.driveLink, target: "_blank", rel: "noreferrer", className: "text-lime-700 underline", children: ["\u2713 ", u.fileName] }) }, i))) })), _jsx("input", { type: "file", multiple: true, onChange: (e) => handleFileChange(doc.key, e.target.files), className: "text-xs w-full" }), staged.length > 0 && (_jsxs("p", { className: "text-xs text-slate-500 mt-1", children: [staged.length, " file", staged.length === 1 ? '' : 's', " selected \u2014 not submitted yet"] }))] }, doc.key));
                        }) }), _jsx("div", { className: "sticky bottom-0 mt-6 pt-4 pb-6 bg-gradient-to-t from-white via-white to-transparent", children: _jsx("button", { type: "button", onClick: handleSubmit, disabled: totalStaged === 0 || submitting, className: "w-full bg-slate-800 hover:bg-slate-900 disabled:opacity-40 text-white font-semibold py-3 rounded-lg transition", children: submitting
                                ? 'Submitting…'
                                : totalStaged > 0
                                    ? `Submit ${totalStaged} Document${totalStaged === 1 ? '' : 's'}`
                                    : 'Select documents to submit' }) })] })] }));
}
